import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import StarField from './components/StarField';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import GateSection from './components/GateSection';
import ProgressBar from './components/ProgressBar';
import ProblemSection from './components/ProblemSection';
import SolutionSection from './components/SolutionSection';
import AwarenessSection from './components/AwarenessSection';
import ConsiderationSection from './components/ConsiderationSection';
import AutomationSection from './components/AutomationSection';
import ConversionSection from './components/ConversionSection';
import LoyaltySection from './components/LoyaltySection';
import CaseStudySection from './components/CaseStudySection';
import WhyItWorksSection from './components/WhyItWorksSection';
import ServicesSection from './components/ServicesSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import './App.css';

const STORAGE_KEY = 'zaharlab_access_granted';

function HomePage() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check localStorage on mount
  // Note: This stores only a boolean flag for guide access, not sensitive auth data
  // Using localStorage is acceptable here as it's non-sensitive UI state
  useEffect(() => {
    try {
      const accessGranted = localStorage.getItem(STORAGE_KEY);
      if (accessGranted === 'true') {
        setIsUnlocked(true);
      }
    } catch (e) {
      // localStorage may be blocked in some browsers
    }
    setIsLoading(false);
  }, []);

  // Handle successful form submission
  const handleUnlock = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {
      // localStorage may be blocked
    }
    setIsUnlocked(true);
    
    // Smooth scroll to content after a brief delay
    setTimeout(() => {
      const problemSection = document.querySelector('#problem');
      if (problemSection) {
        problemSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#030303]">
      {/* Background particle effect */}
      <StarField />
      
      {/* Noise texture overlay */}
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <Navbar isUnlocked={isUnlocked} />
      
      {/* Progress Bar - Only shows when unlocked and scrolling */}
      <ProgressBar isUnlocked={isUnlocked} />
      
      {/* Main content */}
      <main>
        {/* Always visible */}
        <HeroSection isUnlocked={isUnlocked} onScrollToGate={() => {
          const gateSection = document.querySelector('#unlock-guide');
          if (gateSection) {
            gateSection.scrollIntoView({ behavior: 'smooth' });
          }
        }} />
        
        {/* Gate Section - Lead Capture */}
        {!isUnlocked && (
          <GateSection onUnlock={handleUnlock} />
        )}
        
        {/* Gated Content - Only visible after form submission */}
        {isUnlocked && (
          <>
            <ProblemSection />
            <SolutionSection />
            <AwarenessSection />
            <ConsiderationSection />
            <AutomationSection />
            <ConversionSection />
            <LoyaltySection />
            <CaseStudySection />
            <WhyItWorksSection />
            <ServicesSection />
            <CTASection />
          </>
        )}
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* Toast notifications */}
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0a0a0a',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#ffffff',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
